import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useBranding } from '@/lib/branding/BrandingProvider';

export interface NeuraxLicense {
  id: string;
  license_key: string;
  status: string;
  license_type: string;
  machine_id: string | null;
  last_used_at: string | null;
  created_at: string;
  valid_until: string | null;
  b2b_end_client_id: string | null;
  assigned_to_employee_id: string | null;
  notes: string | null;
  end_client: { id: string; name: string; email: string } | null;
  assigned_to_employee: { id: string; full_name: string | null; email: string } | null;
}

const LICENSE_SELECT = `
  id,
  license_key,
  status,
  license_type,
  machine_id,
  last_used_at,
  created_at,
  valid_until,
  b2b_end_client_id,
  assigned_to_employee_id,
  notes
`;

async function fetchLicenses(companyId: string): Promise<NeuraxLicense[]> {
  // neurax_licenses has CHECK (at most one of client_id/b2b_company_id/b2b_end_client_id).
  // Per-candidate licenses don't have b2b_company_id set, so we scope by candidate id.
  // Step 1: get all of this company's candidate ids.
  const { data: candidates, error: candErr } = await supabase
    .from('b2b_end_clients')
    .select('id, name, email')
    .eq('b2b_company_id', companyId);
  if (candErr) throw new Error(`b2b_end_clients: ${candErr.message}`);

  const candidatesById: Record<string, { id: string; name: string; email: string }> = {};
  const candidateIds: string[] = [];
  for (const c of candidates ?? []) {
    candidatesById[c.id] = c as { id: string; name: string; email: string };
    candidateIds.push(c.id);
  }

  if (candidateIds.length === 0) return [];

  // Step 2: licenses where b2b_end_client_id ∈ company's candidate ids OR b2b_company_id matches
  // (the OR covers company-pool licenses and employee_demo licenses, which are scoped to the
  // company via b2b_company_id with b2b_end_client_id NULL).
  const { data: licenses, error: licErr } = await supabase
    .from('neurax_licenses')
    .select(LICENSE_SELECT)
    .or(`b2b_company_id.eq.${companyId},b2b_end_client_id.in.(${candidateIds.join(',')})`)
    .order('created_at', { ascending: false });

  if (licErr) throw new Error(`neurax_licenses: ${licErr.message}`);

  // Resolve assigned_to_employee_id → b2b_employees in a single round-trip
  const employeeIds = Array.from(
    new Set(
      (licenses ?? [])
        .map((l: { assigned_to_employee_id: string | null }) => l.assigned_to_employee_id)
        .filter((id): id is string => !!id)
    )
  );
  const employeesById: Record<string, { id: string; full_name: string | null; email: string }> = {};
  if (employeeIds.length > 0) {
    const { data: employees } = await supabase
      .from('b2b_employees')
      .select('id, full_name, email')
      .in('id', employeeIds);
    for (const e of employees ?? []) {
      employeesById[e.id] = e as { id: string; full_name: string | null; email: string };
    }
  }

  return ((licenses ?? []) as Omit<NeuraxLicense, 'end_client' | 'assigned_to_employee'>[]).map(
    (l) => ({
      ...l,
      end_client: l.b2b_end_client_id ? (candidatesById[l.b2b_end_client_id] ?? null) : null,
      assigned_to_employee: l.assigned_to_employee_id
        ? (employeesById[l.assigned_to_employee_id] ?? null)
        : null,
    })
  ) as NeuraxLicense[];
}

export function useLicenses() {
  const { companyId } = useBranding();
  return useQuery({
    queryKey: ['neurax', 'licenses', companyId],
    queryFn: () => fetchLicenses(companyId),
    staleTime: 30_000,
  });
}
